import axios from 'axios';
import { ORDER_GENIE_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

const client = axios.create({
  baseURL: ORDER_GENIE_API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  // getAuthHeadersFromStorage() already prioritizes OAuth over API key and
  // returns the right single credential — don't also attach a (possibly
  // stale) X-API-Key on top, or an OAuth request can carry a leftover key
  // the backend rejects even though the Bearer token is valid.
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders).length) config.headers = { ...authHeaders, ...config.headers };
  return config;
});
attachAuthInterceptor(client);

function extractError(err) {
  const d = err?.response?.data;
  if (d) {
    if (typeof d === 'string') return d;
    return d.detail || d.error || d.message || JSON.stringify(d);
  }
  if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) return 'Request timed out.';
  return err?.message || 'Unexpected error';
}

export const getMailboxConfig = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/mailbox/config');
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const saveMailboxConfig = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/mailbox/config', payload);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const testMailboxConnection = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/mailbox/test-connection', payload);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const syncMailboxNow = async () => {
  try {
    const { data } = await client.post('/api/v1/orderx/mailbox/sync-now');
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const getMailboxSyncStatus = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/mailbox/sync-status');
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const listInboxItems = async ({
  category, status, search, sender, dateFrom, dateTo, deleted, page = 1, pageSize = 25,
} = {}) => {
  try {
    const params = { page, page_size: pageSize };
    if (category) params.category = category;
    if (status) params.status = status;
    if (search) params.search = search;
    if (sender) params.sender = sender;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (deleted) params.deleted = true;
    const { data } = await client.get('/api/v1/orderx/mailbox/inbox', { params });
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const searchInboxSenders = async (search, limit = 20) => {
  try {
    const params = { limit };
    if (search) params.search = search;
    const { data } = await client.get('/api/v1/orderx/mailbox/inbox/senders', { params });
    return data.senders || [];
  } catch (err) { throw new Error(extractError(err)); }
};

export const getInboxItem = async (itemId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/mailbox/inbox/${itemId}`);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const reprocessInboxItem = async (itemId) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/mailbox/inbox/${itemId}/reprocess`);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const deleteInboxItem = async (itemId) => {
  try {
    const { data } = await client.delete(`/api/v1/orderx/mailbox/inbox/${itemId}`);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const bulkDeleteInboxItems = async (itemIds) => {
  try {
    const { data } = await client.post('/api/v1/orderx/mailbox/inbox/bulk-delete', { item_ids: itemIds });
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const restoreInboxItem = async (itemId) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/mailbox/inbox/${itemId}/restore`);
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const bulkRestoreInboxItems = async (itemIds) => {
  try {
    const { data } = await client.post('/api/v1/orderx/mailbox/inbox/bulk-restore', { item_ids: itemIds });
    return data;
  } catch (err) { throw new Error(extractError(err)); }
};

export const downloadAttachment = async (itemId, attachmentId, filename) => {
  const resp = await client.get(
    `/api/v1/orderx/mailbox/inbox/${itemId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
